import React, { useRef, useState, useEffect } from 'react';
import { createAnimation } from '@ionic/react';
import { pxToRem } from '@/modules/config/constants';

interface Props {
  count: number;
}

const FLAKE_POSITIONS = [
  { left: '10%', top: '60%' },
  { left: '30%', top: '80%' },
  { left: '50%', top: '70%' },
  { left: '70%', top: '85%' },
  { left: '85%', top: '60%' },
];

function flight(from: HTMLElement, to: HTMLElement) {
  const fromRect = from.getBoundingClientRect();
  const toRect = to.getBoundingClientRect();
  const dx =
    toRect.left + toRect.width / 2 - (fromRect.left + fromRect.width / 2);
  const dy =
    toRect.top + toRect.height / 2 - (fromRect.top + fromRect.height / 2);

  return createAnimation()
    .addElement(from)
    .duration(400)
    .easing('ease-in')
    .fromTo(
      'transform',
      'translate(0, 0) scale(1)',
      `translate(${dx}px, ${dy}px) scale(0.3)`
    )
    .fromTo('opacity', '1', '0');
}

function pop(el: HTMLElement) {
  return createAnimation()
    .addElement(el)
    .duration(150)
    .easing('ease-out')
    .keyframes([
      { offset: 0, transform: 'scale(1)' },
      { offset: 0.5, transform: 'scale(1.3)' },
      { offset: 1, transform: 'scale(1)' },
    ]);
}

const RewardCounter: React.FC<Props> = ({ count }) => {
  const FLAKE_COUNT = Math.min(count, 5);
  const hudRef = useRef<HTMLDivElement>(null);
  const flakeRef0 = useRef<HTMLSpanElement>(null);
  const flakeRef1 = useRef<HTMLSpanElement>(null);
  const flakeRef2 = useRef<HTMLSpanElement>(null);
  const flakeRef3 = useRef<HTMLSpanElement>(null);
  const flakeRef4 = useRef<HTMLSpanElement>(null);
  const allFlakeRefs = [flakeRef0, flakeRef1, flakeRef2, flakeRef3, flakeRef4];
  const flakeRefs = allFlakeRefs.slice(0, FLAKE_COUNT);

  const [displayCount, setDisplayCount] = useState(0);

  useEffect(() => {
    if (count === 0 || FLAKE_COUNT === 0) return;
    (async () => {
      for (let i = 0; i < flakeRefs.length; i++) {
        const from = flakeRefs[i].current;
        const to = hudRef.current;
        if (from && to) {
          await flight(from, to).play();
          setDisplayCount(c => c + Math.ceil(count / FLAKE_COUNT));
          if (to) pop(to).play();
        }
      }
      setDisplayCount(count);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        width: '200px',
        height: '160px',
        margin: '0 auto',
      }}
    >
      <div
        ref={hudRef}
        style={{
          position: 'absolute',
          top: '0',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--ion-color-success)',
          color: '#fff',
          borderRadius: '24px',
          padding: '8px 20px',
          fontSize: pxToRem(28),
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        ❄ {displayCount}
      </div>
      {FLAKE_POSITIONS.slice(0, FLAKE_COUNT).map((pos, i) => (
        <span
          key={i}
          ref={allFlakeRefs[i]}
          style={{
            position: 'absolute',
            left: pos.left,
            top: pos.top,
            fontSize: pxToRem(28),
            lineHeight: 1,
          }}
        >
          ❄
        </span>
      ))}
    </div>
  );
};

export default RewardCounter;
